import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
  Divider,
  IconButton,
} from '@mui/material';
import {
  Close as CloseIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Description as DescriptionIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { ko } from 'date-fns/locale';
import { MoldHistory, HISTORY_TYPE_LABELS } from '../../types/history.types';
import { Mold } from '../../types';

interface HistoryDetailModalProps {
  open: boolean;
  onClose: () => void;
  history: MoldHistory | null;
  molds?: Mold[];
  onEdit: () => void;
  onDelete: () => void;
}

const HistoryDetailModal: React.FC<HistoryDetailModalProps> = ({
  open,
  onClose,
  history,
  molds = [],
  onEdit,
  onDelete,
}) => {
  if (!history) return null;

  const formatDate = (date: Date | undefined) => {
    if (!date) return '-';
    return format(date, 'PPP', { locale: ko });
  };

  const formatCurrency = (amount: number | undefined) => {
    if (!amount) return '-';
    return `₩${amount.toLocaleString()}`;
  };

  // 캐비티 정보 포맷팅 함수
  const formatCavityInfo = (selectedCavities: number[] | undefined) => {
    if (!selectedCavities || selectedCavities.length === 0) return '-';
    
    const mold = molds.find(m => m.id === history.moldId);
    if (!mold || !mold.cavities || mold.cavities.length === 0) {
      // 캐비티 정보 없으면 번호만 표시
      return selectedCavities.join(', ');
    }

    // 캐비티 번호: 제품명 형식으로 표시
    return selectedCavities
      .map(num => {
        const cavity = mold.cavities?.find(c => c.cavityNumber === num);
        return cavity?.productName 
          ? `${num}: ${cavity.productName}`
          : `${num}`;
      })
      .join(', ');
  };

  const InfoRow: React.FC<{ label: string; value: React.ReactNode; fullWidth?: boolean }> = ({
    label,
    value,
    fullWidth,
  }) => (
    <Box sx={{ flex: fullWidth ? '1 1 100%' : '1 1 auto', minWidth: fullWidth ? '100%' : { xs: '100%', md: '48%' } }}>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
      <Typography variant="body1" component="div">{value}</Typography>
    </Box>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">이력 상세</Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* 기본 정보 */}
          <Box>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              기본 정보
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              <InfoRow label="금형 번호" value={history.moldNumber} />
              <InfoRow label="금형명" value={history.moldName || '-'} />
              <InfoRow 
                label="유형" 
                value={<Chip label={HISTORY_TYPE_LABELS[history.type]} color="primary" size="small" />} 
              />
              <InfoRow label="날짜" value={formatDate(history.date)} />
              <InfoRow label="제목" value={history.title} fullWidth />
              <InfoRow 
                label="설명" 
                value={
                  <Typography sx={{ whiteSpace: 'pre-wrap' }}>
                    {history.description || '-'}
                  </Typography>
                }
                fullWidth 
              />
            </Box>
          </Box>

          {/* 출고/입고 정보 */}
          {(history.type === 'outbound' || history.type === 'inbound') && (
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                출고/입고 정보
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                {history.shots !== undefined && (
                  <InfoRow label="쇼트 수" value={history.shots.toLocaleString()} />
                )}
                {history.purpose && (
                  <InfoRow label="목적" value={history.purpose} />
                )}
                {history.expectedReturn && (
                  <InfoRow label="예상 반납일" value={formatDate(history.expectedReturn)} />
                )}
                {history.actualReturn && (
                  <InfoRow label="실제 반납일" value={formatDate(history.actualReturn)} />
                )}
              </Box>
            </Box>
          )}

          {/* 생산 정보 */}
          {(history.type === 'production' || history.type === 'trial_production') && (
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                생산 정보
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                {history.productionQty !== undefined && (
                  <InfoRow label="생산량" value={history.productionQty.toLocaleString()} />
                )}
                {history.defectQty !== undefined && (
                  <InfoRow label="불량 수" value={history.defectQty.toLocaleString()} />
                )}
                {history.selectedCavities && history.selectedCavities.length > 0 && (
                  <InfoRow 
                    label="사용 패밀리" 
                    value={formatCavityInfo(history.selectedCavities)} 
                    fullWidth
                  />
                )}
              </Box>
            </Box>
          )}

          {/* 정비/수리/개조 정보 */}
          {['maintenance', 'repair', 'modification'].includes(history.type) && (
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                정비/수리 정보
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                {history.cost !== undefined && (
                  <InfoRow label="비용" value={formatCurrency(history.cost)} />
                )}
                {history.vendor && (
                  <InfoRow label="업체" value={history.vendor} />
                )}
              </Box>
            </Box>
          )}

          {/* 점검 정보 */}
          {history.type === 'inspection' && (
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                점검 정보
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                {history.inspector && (
                  <InfoRow label="점검자" value={history.inspector} />
                )}
                {history.inspectionResult && (
                  <InfoRow 
                    label="결과" 
                    value={
                      <Chip
                        label={history.inspectionResult === 'normal' ? '정상' : '이상'}
                        color={history.inspectionResult === 'normal' ? 'success' : 'error'}
                        size="small"
                      />
                    }
                  />
                )}
              </Box>
            </Box>
          )}

          {/* 폐기 정보 */}
          {history.type === 'disposal' && (
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                폐기 정보
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                {history.disposalReason && (
                  <InfoRow label="폐기 사유" value={history.disposalReason} fullWidth />
                )}
                {history.disposalMethod && (
                  <InfoRow label="폐기 방법" value={history.disposalMethod} fullWidth />
                )}
              </Box>
            </Box>
          )}

          {/* 첨부파일 */}
          {history.files && history.files.length > 0 && (
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                첨부파일 ({history.files.length})
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                {history.files.map((file) => (
                  <Box
                    key={file.id}
                    sx={{
                      width: 120,
                      height: 120,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      overflow: 'hidden',
                      position: 'relative',
                      cursor: 'pointer',
                      '&:hover': { opacity: 0.8 },
                    }}
                    onClick={() => window.open(file.url, '_blank')}
                  >
                    {file.type === 'image' ? (
                      <img
                        src={file.url}
                        alt={file.name}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    ) : (
                      <Box
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        justifyContent="center"
                        height="100%"
                        p={1}
                      >
                        <DescriptionIcon sx={{ fontSize: 40, color: 'text.secondary' }} />
                        <Typography
                          variant="caption"
                          align="center"
                          sx={{
                            mt: 1,
                            wordBreak: 'break-all',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                          }}
                        >
                          {file.name}
                        </Typography>
                      </Box>
                    )}
                    <IconButton
                      size="small"
                      sx={{
                        position: 'absolute',
                        top: 4,
                        right: 4,
                        bgcolor: 'background.paper',
                        '&:hover': { bgcolor: 'action.hover' },
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        window.open(file.url, '_blank');
                      }}
                    >
                      <DownloadIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          {/* 메타 정보 */}
          <Box>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              기록 정보
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              <InfoRow label="작성자" value={history.userName || '-'} />
              <InfoRow label="작성일" value={formatDate(history.createdAt)} />
              {history.updatedAt && history.updatedAt !== history.createdAt && (
                <InfoRow label="최종 수정일" value={formatDate(history.updatedAt)} />
              )}
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onDelete} color="error" startIcon={<DeleteIcon />} sx={{ color: 'white' }}>
          삭제
        </Button>
        <Button onClick={onEdit} variant="contained" startIcon={<EditIcon />} sx={{ color: 'white' }}>
          수정
        </Button>
        <Button onClick={onClose} sx={{ color: 'white' }}>닫기</Button>
      </DialogActions>
    </Dialog>
  );
};

export default HistoryDetailModal;
